import { ReactNode, useMemo } from "react";
import { Section } from "views/applications-id-report/components/Section";
import {
  CreditReportConsumerInformation,
  CreditReportAlertAndTriggers
} from "types/landlord/reports";
import { NotEqualSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { SsnDisplay } from "shared/components/SsnDisplay";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { formatDate } from "views/applications-id-report/utils";
import { DataVendors } from "shared/types";

type Warning = {
  title: string;
  value: string | ReactNode;
  scrollToSection?: () => void;
};

type WarningsProps = {
  applicationId: string;
  applicantId: string;
  verificationId: string;
  consumer_information?: CreditReportConsumerInformation;
  alerts_and_triggers?: CreditReportAlertAndTriggers;
  ssn_mismatched?: boolean;
  itin_detected?: boolean;
  provided_ssn?: string;
  equifax_ssn?: string;
  provided_address?: string;
  equifax_address?: string;
  vendor?: DataVendors;
};

export const Warnings = ({
  applicationId,
  applicantId,
  verificationId,
  consumer_information,
  alerts_and_triggers,
  ssn_mismatched,
  itin_detected,
  provided_ssn,
  equifax_ssn,
  provided_address,
  equifax_address,
  vendor
}: WarningsProps) => {
  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  const vendorName = useMemo(() => {
    if (vendor === DataVendors.TransUnion) return "TransUnion";
    if (vendor === DataVendors.Equifax) return "Equifax";
    return "Equifax";
  }, [vendor]);

  const warnings = useMemo(() => {
    const warnings: Warning[] = [];

    if (equifax_ssn && itin_detected)
      warnings.push({
        title: "Applicant's tax identifier is an ITIN",
        value: (
          <div className="flex flex-wrap items-center gap-2 text-sm text-content-400 font-normal">
            <span>SSN from {vendorName}:</span>
            <SsnDisplay
              displayValue={(data) => data.metadata?.personal_data?.ssn}
              applicationId={applicationId}
              applicantId={applicantId}
              verificationId={verificationId}
              maskedSsn={equifax_ssn}
            />
          </div>
        ),
        scrollToSection: () => scrollToSection("customer-inquiry")
      });

    if (ssn_mismatched && provided_ssn && equifax_ssn)
      warnings.push({
        title: `SSN provided by the applicant does not match the SSN received from ${vendorName}`,
        value: (
          <div className="flex flex-wrap items-center gap-2 text-sm text-content-400 font-normal">
            <span>Provided SSN:</span>
            <SsnDisplay
              applicationId={applicationId}
              applicantId={applicantId}
              verificationId={verificationId}
              maskedSsn={provided_ssn}
            />

            <HugeiconsIcon
              icon={NotEqualSignIcon}
              size={20}
              className="text-content-100 shrink-0"
            />

            <span>Response from {vendorName}:</span>
            <SsnDisplay
              displayValue={(data) => data.metadata?.personal_data?.ssn}
              applicationId={applicationId}
              applicantId={applicantId}
              verificationId={verificationId}
              maskedSsn={equifax_ssn}
            />
          </div>
        ),
        scrollToSection: () => scrollToSection("customer-inquiry")
      });

    if (consumer_information?.death_date)
      warnings.push({
        title: "Credit report is for a deceased individual",
        value: `Death date: ${formatDate(consumer_information.death_date) || "--"}`,
        scrollToSection: () => scrollToSection("consumer-information")
      });

    if (alerts_and_triggers?.address_discrepancy_indicator === "Y")
      warnings.push({
        title: `Address provided by the applicant does not match the address received from ${vendorName}`,
        value: (
          <div className="flex flex-wrap items-center gap-2 text-sm text-content-400 font-normal">
            <span>Provided address:</span>
            <span>{provided_address || "--"}</span>
            <HugeiconsIcon
              icon={NotEqualSignIcon}
              size={20}
              className="text-content-100 shrink-0"
            />
            <span>Response from {vendorName}:</span>
            <span>{equifax_address || "--"}</span>
          </div>
        ),
        scrollToSection: () => scrollToSection("consumer-information")
      });

    if (alerts_and_triggers?.fraud_victim_indicator)
      warnings.push({
        title: "Applicant has a fraud victim alert",
        value: "A fraud alert was identified on the applicant's credit report",
        scrollToSection: () => scrollToSection("alerts-and-triggers")
      });

    return warnings;
  }, [
    applicationId,
    applicantId,
    verificationId,
    consumer_information?.death_date,
    alerts_and_triggers?.address_discrepancy_indicator,
    alerts_and_triggers?.fraud_victim_indicator,
    ssn_mismatched,
    itin_detected,
    provided_ssn,
    equifax_ssn,
    provided_address,
    equifax_address,
    vendorName
  ]);

  if (warnings.length === 0) return null;

  return (
    <Section
      sectionTitle="Warnings"
      maxWidth="none"
      sectionClassName="pt-4 pb-12"
    >
      <div>
        {warnings.map((warning, index) => (
          <div key={index} className="px-6 py-4 flex items-center gap-3">
            <HugeiconsIcon
              icon={AlertCircleIcon}
              size={20}
              className="text-warning-600"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-content-1000 mb-1">
                {warning.title}
              </p>
              <div className="text-sm text-content-400 font-normal">
                {warning.value}
              </div>
            </div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              className="shrink-0"
              onClick={warning.scrollToSection}
            >
              Review
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
};
